import { Icon } from "@iconify/react";
import styled from "styled-components";
import { ContactStore } from "../../../assets/store/contact.store";
import { ServicesStore } from "../../../assets/store/service.store";
import { SocialsStore } from "../../../assets/store/social.store";
import PrimaryButton from "../../../common/components/button/PrimaryButton";
import FileDropZone from "../../../common/components/dropzone/FileDropZone";
import Eyebrow from "../../../common/components/eyebrow/Eyebrow";
import PrimaryDropdown from "../../../common/components/input/PrimaryDropdown";
import PrimaryInput from "../../../common/components/input/PrimaryInput";
import PrimaryTextArea from "../../../common/components/input/PrimaryTextArea";
import { HeadingBlock } from "../../../common/components/layout/HeadingBlock";
import Description from "../../../common/components/text/Description";
import H2 from "../../../common/components/text/H2";
import { border } from "../../../common/themes/border.theme";
import { color } from "../../../common/themes/color.thems";
import { Theme } from "../../../common/themes/theme";

const Wrapper = styled.section`
  background: ${Theme.color.background.light};
  font-family: ${Theme.font.fontFamily.sans};
  border-bottom: 1px solid ${Theme.color.border.light};
  border-top: 1px solid ${Theme.color.border.light};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  min-height: 580px;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div`
  padding: 64px 50px 56px;
  background: ${Theme.color.surface.light};
  border-right: 1px solid ${Theme.color.border.light};
  display: flex;
  flex-direction: column;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    padding: 48px 20px;
    border-right: none;
    border-bottom: 1px solid ${Theme.color.border.light};
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 24px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 0;
  border-bottom: 1px solid ${Theme.color.border.light};
`;

const InfoIcon = styled.div`
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  background: ${Theme.color.surface.red};
  border: 1px solid ${Theme.color.background.red};
  border-radius: ${Theme.border.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.color.text.primary};
  font-size: 17px;
`;

const InfoLabel = styled.p`
  font-size: ${Theme.font.fontSize.xs};
  font-weight: ${Theme.font.fontWeight.medium};
  letter-spacing: ${Theme.font.letterSpacing.widest};
  text-transform: uppercase;
  color: ${Theme.color.text.secondary};
  margin: 0 0 4px;
`;

const InfoValue = styled.div`
  font-size: ${Theme.font.fontSize.base};
  font-weight: ${Theme.font.fontWeight.regular};
  color: ${Theme.color.text.heading};
  line-height: ${Theme.font.lineHeight.relaxed};

  a {
    color: ${Theme.color.text.neutral};
    text-decoration: none;
    transition: color 0.15s ease;
    &:hover {
      color: ${Theme.color.text.primary};
    }
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 28px;
`;

const SocialBtn = styled.div`
  width: 32px;
  height: 32px;
  background: ${color.background.light};
  /* border: 1px solid ${color.border.light}; */
  border: none;
  border-radius: ${border.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.text.secondary};
  cursor: pointer;

  &:hover {
    border-color: ${color.text.primary};
    color: ${color.text.inverse};
    img {
      max-height: 30px;
    }
  }
  img {
    max-height: 28px;
    transition-duration: 0.2s;
  }
`;

const Right = styled.div`
  padding: 64px 50px;
  background: ${Theme.color.background.light};

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    padding: 48px 20px;
  }
`;

const FormTitle = styled.h3`
  font-family: ${Theme.font.fontFamily.heading[0]}, sans-serif;
  font-size: ${Theme.font.fontSize.xxl};
  font-weight: ${Theme.font.fontWeight.bold};
  text-transform: uppercase;
  color: ${Theme.color.text.heading};
  letter-spacing: ${Theme.font.letterSpacing.wide};
  margin: 0 0 6px;

  span {
    color: ${Theme.color.text.primary};
  }
`;

const FormSub = styled.p`
  font-size: ${Theme.font.fontSize.sm};
  color: ${Theme.color.text.secondary};
  margin: 0 0 28px;
  line-height: ${Theme.font.lineHeight.relaxed};
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    grid-template-columns: 1fr;
  }
`;

const Note = styled.p`
  margin: 2px 0 0;
  font-size: ${Theme.font.fontSize.xs};
  color: ${Theme.color.text.secondary};
  line-height: ${Theme.font.lineHeight.relaxed};
`;

function ContactSection() {
  return (
    <Wrapper>
      <Grid>
        <Left>
          <HeadingBlock>
            <Eyebrow text={"Get in Touch"} />
            <H2>
              Let's Build
              <br />
              Something <span>Precise.</span>
            </H2>
            <Description>
              Send us your requirements and our engineering team will respond
              with a quote
            </Description>
          </HeadingBlock>
          <InfoList>
            {ContactStore.map((item) => (
              <InfoItem key={item.label}>
                <InfoIcon>
                  <Icon icon={item.icon} />
                </InfoIcon>

                <div>
                  <InfoLabel>{item.label}</InfoLabel>

                  {item.values.map((val, idx) => (
                    <InfoValue key={idx}>{val}</InfoValue>
                  ))}
                </div>
              </InfoItem>
            ))}
          </InfoList>
          <SocialRow>
            {SocialsStore.map((s) => (
              <SocialBtn key={s.label} title={s.label}>
                <img src={s.icon} alt={s.label} />
              </SocialBtn>
            ))}
          </SocialRow>
        </Left>

        <Right>
          <FormTitle>
            Request a <span>Quote</span>
          </FormTitle>
          <FormSub>
            Fill in your details and upload a drawing — we'll respond as soon as
            we can.
          </FormSub>

          <Form>
            <Row>
              <PrimaryInput
                label={"Full Name *"}
                type={"text"}
                placeholder="John Doe"
              />
              <PrimaryInput
                label={"Company *"}
                type={"text"}
                placeholder="Company Name"
              />
            </Row>
            <Row>
              <PrimaryInput
                label={"Email Address *"}
                type={"email"}
                placeholder="john@company.com"
              />
              <PrimaryInput
                label={"Phone / WhatsApp *"}
                type={"tel"}
                placeholder="+977 98 000 0000"
              />
            </Row>

            <Row>
              <PrimaryDropdown defaultValue="" label={"Service Required"}>
                <option value="" disabled>
                  Select a service
                </option>
                {ServicesStore.map((s) => (
                  <option key={s.id}>{s.title}</option>
                ))}
              </PrimaryDropdown>

              <PrimaryInput
                label={"Quantity"}
                type={"text"}
                placeholder="e.g. 500 pcs"
              />
            </Row>

            <PrimaryTextArea
              rows={4}
              required={true}
              label={"Part Description / Requirements"}
              placeholder={
                "Describe your part, material, tolerance requirements, surface finish, or any special notes..."
              }
            />
            <FileDropZone />
            <PrimaryButton>Send Request</PrimaryButton>
            <Note>Your drawings and data are kept strictly confidential.</Note>
          </Form>
        </Right>
      </Grid>
    </Wrapper>
  );
}

export default ContactSection;
